import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../../locales/translations';
import { ColumnTable, Icon } from '@octano/global-ui';
import { AdmissionScope, Offer, OfferListTable, Process } from '../types';
import { formatDate } from '../../../../utils/dates';

interface UseOffersColumnsProps {
  toggleEditOffers: (offer?: Offer) => void;
  process: Process;
}

export function useOffersColumns({
  toggleEditOffers,
  process,
}: UseOffersColumnsProps) {
  const { t } = useTranslation(TranslationsKeys.CONFIGURATION_ACADEMIC_OFFER);

  const texts = useMemo(() => {
    return {
      offer: t('offersList.columns.offer'),
      startDate: t('offersList.columns.startDate'),
      duration: t('offersList.columns.duration'),
      enrollmentType: t('offersList.columns.enrollmentType'),
      edit: t('offersList.columns.edit'),
    };
  }, [t]);

  const columns: ColumnTable<OfferListTable>[] = useMemo(() => {
    return [
      {
        columnName: 'offer',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        headerText: texts.offer,
        cellFormat: ({ row }) => {
          return `${row.studyPlanName} - ${row.campusName} - ${row.scheduleName}`;
        },
      },
      {
        columnName: 'startDate',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        headerText: texts.startDate,
        cellFormat: ({ row }) => {
          return row.startDate ? formatDate(row.startDate, 'DD/MM/YYYY') : '-';
        },
      },
      {
        columnName: 'duration',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        headerText: texts.duration,
        cellFormat: ({ row }) => {
          return row.duration ? row.duration : '-';
        },
      },
      {
        columnName: 'admissionScopes',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        headerText: texts.enrollmentType,
        cellFormat: ({ row }) => {
          if (row.admissionScopes.length === 0) {
            return '-';
          }

          const translatedScopes = row.admissionScopes.map(
            (scope: AdmissionScope) => {
              return scope === AdmissionScope.OS ? 'Presencial' : 'Online';
            },
          );

          return translatedScopes.join(' - ');
        },
      },
      {
        columnName: 'edit',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        headerText: texts.edit,
        cellFormat: ({ row }) => {
          return (
            <span onClick={() => toggleEditOffers(row as Offer)}>
              <Icon name="edit" color="primary" className="cursor-pointer" />
            </span>
          );
        },
      },
    ];
  }, [texts, toggleEditOffers]);

  return columns;
}
